import Cross from "@assets/icons/cross.svg";
import { Button } from "@components/button";
import classNames from "classnames";
import {
  memo,
  PropsWithChildren,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import styles from "./modal.module.scss";

export type ModalProps = PropsWithChildren<{
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  subTitle?: string;
  className?: string;
  bodyClassName?: string;
}>;

export const Modal = memo((props: ModalProps) => {
  if (!props.isOpen) return null;

  return <InnerModal {...props} />;
});

const InnerModal = memo(
  ({
    onClose,
    title,
    subTitle,
    children,
    className,
    bodyClassName,
  }: ModalProps) => {
    const isDraggingFromModal = useRef(false);

    const close = useCallback(() => {
      if (!isDraggingFromModal.current) {
        onClose();
        isDraggingFromModal.current = false;
      }
    }, [onClose]);

    const handleMouseDown = useCallback(
      (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        isDraggingFromModal.current = true;
        e.stopPropagation();
      },
      []
    );

    const handleMouseUp = useCallback(() => {
      isDraggingFromModal.current = false;
    }, []);

    const modalBodyRef = useRef<HTMLDivElement>(null);
    const [paddingRight, setPaddingRight] = useState(0);

    useEffect(() => {
      const checkScroll = () => {
        if (modalBodyRef.current) {
          const hasScroll =
            modalBodyRef.current.scrollHeight >
            modalBodyRef.current.clientHeight;
          setPaddingRight(hasScroll ? 16 : 0);
        }
      };
      checkScroll();
      window.addEventListener("resize", checkScroll);

      return () => {
        window.removeEventListener("resize", checkScroll);
      };
    }, []);

    return (
      <div className={styles.modalOverlay} onClick={close} onMouseDown={close}>
        <div
          className={classNames(styles.modal, className)}
          onClick={(e) => e.stopPropagation()}
          onMouseUp={handleMouseUp}
          onMouseDown={handleMouseDown}
        >
          {title && (
            <div className={styles.modalHeader}>
              <div className={styles.modalTitle}>
                <p className={styles.title}>{title}</p>
                {!!subTitle && <p className={styles.subTitle}>{title}</p>}
              </div>
              <Button
                iconClassName={styles.closeButton}
                icon={Cross}
                onClick={onClose}
              />
            </div>
          )}
          <div
            className={classNames(styles.modalBody, bodyClassName)}
            ref={modalBodyRef}
            style={{ paddingRight: `${paddingRight}px` }}
          >
            {children}
          </div>
        </div>
      </div>
    );
  }
);
